import React, { useEffect, useMemo, useCallback } from "react";
import { Box, Snackbar, useMediaQuery, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import YourDetails from "./YourDetails";
import FeedbackDetails from "./FeedbackDetails";
import { createEnquete } from "../../api/feedback.service";
import { LoadingScreen } from "./components/LoadingScreen";
import { AlreadySubmittedCard } from "./components/AlreadySubmittedCard";
import { FeedbackStepper } from "./components/FeedbackStepper";
import { NavigationFooter } from "./components/NavigationFooter";
import { useFeedbackData } from "./hooks/useFeedbackData";
import { useFeedbackCss } from "./hooks/useFeedbackCss";
import { postToParent, hideIframe, showFullIframe } from "./utils/postMessage";
import {
  getModuleIdFromReservationType,
  getModuleReservationField,
} from "./utils/moduleHelpers";
import { fout } from '../../lib/logboek';

/* ─── Main component ────────────────────────────────────────────── */
export default function FeedbackComponent() {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const { feedBackDetails, addFields, basicLanguages } = useSelector(
    (s) => s.feedback
  );
  const { isAllowPost, postAnoumous } = useSelector((s) => s.feedback);
  
  const feedbackCss = useFeedbackCss();

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const secretkey = queryParams.get("secretkey");
  const reservationType = queryParams.get("reservation");
  const moduleID = useMemo(
    () => getModuleIdFromReservationType(reservationType),
    [reservationType]
  );
  const key = queryParams.get("key");

  const { loading, langLoading } = useFeedbackData(
    secretkey,
    key,
    moduleID,
    queryParams
  );

  const [steps, setSteps] = React.useState([]);
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState({ 0: false });
  const [snackOpen, setSnackOpen] = React.useState(false);

  const totalSteps = () => steps.length;
  const completedSteps = () => Object.keys(completed).length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const allStepsCompleted = () => completedSteps() === totalSteps();

  const handleNext = () => {
    const newStep =
      isLastStep() && !allStepsCompleted()
        ? steps.findIndex((_, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newStep);
  };

  const handleBack = () => setActiveStep((p) => p - 1);

  const handleSubmit = useCallback(async () => {
    const reservationField = getModuleReservationField(
      moduleID,
      feedBackDetails
    );
    const payload = {
      data: {
        idcompany: parseInt(feedBackDetails?.idcompany),
        ...reservationField,
        idenquete: parseInt(feedBackDetails?.idenquete),
        isAllowPost,
        isPostAnonymously: postAnoumous,
        fields: Object.values(addFields),
      },
    };

    try {
      const response = await createEnquete(payload);
      if (response?.status === 200) {
        navigate("/successFeedback");
      } else {
        setSnackOpen(true);
      }
    } catch (error) {
      fout("Submit error:", error);
      setSnackOpen(true);
    }
  }, [moduleID, feedBackDetails, isAllowPost, postAnoumous, addFields, navigate]);

  useEffect(() => {
    setSteps([basicLanguages?.Feedback, basicLanguages?.["your details"]]);
  }, [basicLanguages]);

  useEffect(() => {
    document.body.setAttribute(
      "style",
      "background-color: rgb(249,249,249) !important"
    );
  }, []);

  // Early returns for loading and empty states
  if (loading || langLoading) {
    return <LoadingScreen />;
  }

  if (!feedBackDetails || feedBackDetails?.enqueteList?.length <= 0) {
    hideIframe();
    return null;
  }

  if (feedBackDetails?.enqueteSubmitted) {
    return (
      <AlreadySubmittedCard
        basicLanguages={basicLanguages}
        companyLogo={feedBackDetails?.companyLogo}
      />
    );
  }

  // Show full iframe for active feedback
  showFullIframe();

  const isNextDisabled =
    addFields?.length !== feedBackDetails?.enqueteList?.length ||
    !addFields.every((f) => f.rating > 0);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: feedbackCss?.feedbackBgClr || "#f9f9f9",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: { xs: 3, md: 5 },
          px: { xs: 2, md: 3 },
        }}
      >
        {/* ── Header + Stepper ── */}
        <FeedbackStepper
          steps={steps}
          activeStep={activeStep}
          completed={completed}
          feedbackCss={feedbackCss}
          companyLogo={feedBackDetails?.companyLogo}
          isSmall={isSmall}
        />

        {/* ── Step content ── */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 860,
            borderRadius: "20px",
            background: "#ffffff",
            boxShadow: "0 4px 32px rgba(0,0,0,0.07)",
            overflow: "hidden",
            animation: "fadeUp 0.35s ease both",
            "@keyframes fadeUp": {
              from: { opacity: 0, transform: "translateY(16px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Box sx={{ minHeight: 300 }}>
            {activeStep === 0 && <FeedbackDetails Css={feedbackCss} />}
            {activeStep === 1 && <YourDetails Css={feedbackCss} />}
          </Box>

          {/* ── Navigation footer ── */}
          <NavigationFooter
            activeStep={activeStep}
            totalSteps={totalSteps()}
            isNextDisabled={isNextDisabled}
            feedbackCss={feedbackCss}
            basicLanguages={basicLanguages}
            onBack={handleBack}
            onNext={() => {
              if (activeStep === 1) handleSubmit();
              handleNext();
            }}
          />
        </Box>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackOpen}
        autoHideDuration={5000}
        onClose={() => setSnackOpen(false)}
        message="Failed to send feedback — please try again."
        sx={{
          "& .MuiSnackbarContent-root": {
            borderRadius: "12px",
            fontWeight: 500,
          },
        }}
      />
    </>
  );
}
